import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { EmberField } from '@/components/shared/EmberField'
import { DNA_PROFILES, DESTINATIONS } from '@/data/destinations'
import { submitLead } from '@/services/leads'

// "Receive Your Journey" — Sanctum's reverent email-capture gate, inserted
// between the journey map and the journey card. Never blocks the demo: on
// API failure we still proceed to the card.
export function LeadGateScreen() {
  const { dnaKey, selectedDestination, setScreen, email, setEmail, setHasUnlocked } = useAppStore()
  const [value, setValue] = useState(email || '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const profile = DNA_PROFILES[dnaKey]
  const dest = selectedDestination ?? (DESTINATIONS[dnaKey]?.[0] || null)

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)

  const proceed = () => { setHasUnlocked(true); setScreen('card') }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmed = value.trim()
    if (!isValidEmail(trimmed)) { setError('A valid email will let your journey find you.'); return }
    setError('')
    setSubmitting(true)
    setEmail(trimmed)
    await submitLead({
      email: trimmed,
      brand: 'sanctum',
      dna: profile?.name || '',
      destination: dest?.name || '',
    })
    // Never block the demo — proceed regardless of API outcome.
    setSubmitting(false)
    proceed()
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 30%, rgba(201,151,58,0.09) 0%, transparent 55%), linear-gradient(180deg, #081C10 0%, #050E08 100%)', pointerEvents: 'none' }} />
      <EmberField count={18} />

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px' }}>
        <div>
          <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 16, letterSpacing: '0.14em', color: 'var(--candle2)', textTransform: 'uppercase' }}>Sanctum</div>
          <div style={{ fontFamily: 'var(--serif-body)', fontSize: 9, color: 'var(--ivory-faint)', letterSpacing: '0.1em', fontStyle: 'italic', marginTop: -2 }}>by Imaginarium</div>
        </div>
        <button onClick={() => setScreen('journey')} style={{ fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.08em', color: 'var(--candle)', background: 'transparent', border: `1px solid var(--hairline2)`, padding: '6px 14px', cursor: 'pointer', textTransform: 'uppercase' }}>
          ← Back
        </button>
      </div>

      <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 24px 60px' }}>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          style={{
            width: '100%', maxWidth: 460, background: 'rgba(18,48,29,0.55)', border: `1px solid var(--hairline2)`,
            padding: '40px 32px', textAlign: 'center', boxShadow: '0 0 60px rgba(0,0,0,0.4), 0 0 30px rgba(201,151,58,0.06)',
          }}>
          <div style={{ width: 52, height: 52, margin: '0 auto 20px', borderRadius: '50%', border: `1px solid var(--candle)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: 'var(--candle2)', boxShadow: '0 0 20px rgba(201,151,58,0.35)' }}>
            🕯
          </div>
          <h1 style={{ fontFamily: 'var(--serif-caps)', fontSize: 26, color: 'var(--ivory)', letterSpacing: '0.04em', marginBottom: 12 }}>
            Receive Your Journey
          </h1>
          <p style={{ fontFamily: 'var(--serif-body)', fontSize: 14, color: 'var(--ivory-dim)', fontStyle: 'italic', lineHeight: 1.75, marginBottom: 26 }}>
            Leave your email and we will carry your sacred itinerary to you — every stop, every offering, woven with care. A pilgrimage card to keep.
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              type="email"
              value={value}
              onChange={(e) => { setValue(e.target.value); if (error) setError('') }}
              placeholder="your@email.com"
              aria-label="Email address"
              style={{
                width: '100%', background: 'rgba(248,245,239,0.05)', border: `1px solid ${error ? '#C96A3A' : 'var(--hairline2)'}`,
                padding: '13px 16px', fontFamily: 'var(--serif-body)', fontSize: 14, color: 'var(--ivory)', outline: 'none', fontStyle: 'italic',
              }}
            />
            {error && (
              <div style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: '#E29A78', fontStyle: 'italic', textAlign: 'left' }}>{error}</div>
            )}
            <motion.button type="submit" disabled={submitting} whileTap={{ scale: 0.98 }}
              style={{
                width: '100%', padding: 14, background: 'var(--candle)', color: 'var(--forest)',
                fontFamily: 'var(--serif-caps)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase',
                border: 'none', cursor: submitting ? 'wait' : 'pointer', marginTop: 4,
                boxShadow: '0 0 24px rgba(201,151,58,0.35)', opacity: submitting ? 0.7 : 1,
              }}>
              {submitting ? 'Receiving…' : 'Build it →'}
            </motion.button>
          </form>

          <button onClick={proceed} style={{ marginTop: 14, background: 'transparent', border: 'none', color: 'var(--ivory-faint)', fontFamily: 'var(--serif-body)', fontSize: 11, fontStyle: 'italic', cursor: 'pointer', textDecoration: 'underline' }}>
            Continue without receiving it
          </button>

          <p style={{ marginTop: 18, fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'var(--ivory-faint)', textTransform: 'uppercase' }}>
            Private · No spam · Only your journey, held in confidence
          </p>
        </motion.div>
      </div>
    </div>
  )
}
